import { cx } from "../lib/classNames";

// A small set of tinted swatches; pick one deterministically from the name
// so the same entity always gets the same color.
const SWATCHES = [
  "bg-arch-500/20 text-arch-200 ring-arch-500/30",
  "bg-emerald-500/20 text-emerald-200 ring-emerald-500/30",
  "bg-violet-500/20 text-violet-200 ring-violet-500/30",
  "bg-amber-500/20 text-amber-200 ring-amber-500/30",
  "bg-rose-500/20 text-rose-200 ring-rose-500/30",
  "bg-cyan-500/20 text-cyan-200 ring-cyan-500/30",
] as const;

const SIZES = {
  sm: "h-5 w-5 text-[10px]",
  md: "h-6 w-6 text-[11px]",
  lg: "h-8 w-8 text-xs",
} as const;

function swatch(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return SWATCHES[Math.abs(h) % SWATCHES.length]!;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts.at(-1)![0]!).toUpperCase();
}

/**
 * Initials badge with a deterministic tint. `shape="square"` for workspaces /
 * repos, `"circle"` for people.
 */
export function Avatar({
  name,
  size = "md",
  shape = "circle",
  className,
}: {
  readonly name: string;
  readonly size?: keyof typeof SIZES;
  readonly shape?: "circle" | "square";
  readonly className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "inline-flex shrink-0 select-none items-center justify-center font-semibold ring-1 ring-inset",
        shape === "circle" ? "rounded-full" : "rounded-md",
        SIZES[size],
        swatch(name),
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
